import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  TextField,
  Grid,
  Paper,
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { getAllProjects } from '../Servies/AdmiServices';
import Iconify from '../components/iconify';
import TableData from '../components/common/TableData';
import Loader from '../components/common/Loader';

export default function ProjectListPage() {
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const headers = ['Project Name', 'Project Status', 'Deadline', 'Action'];

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setIsLoading(true);
    try {
      const res = await getAllProjects();
      if (res?.data?.status === 'SUCCESS') {
        const projectsData = res?.data?.response?.projects || [];
        setProjects(projectsData);
        setAllProjects(projectsData);
      } else {
        toast.error(res?.data?.message || 'Failed to fetch projects');
      }
    } catch (err) {
      toast.error('Error fetching project data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewProject = () => navigate('/dashboard/manageproject');
  // const handleEditProject = (id) => navigate(`/dashboard/manageproject?projectId=${id}`);
  const handleDeleteProject = (id) => toast.success(`Deleted Successfully..`);

  //  Navigate to ProjectChapters page
  const handleProjectClick = (projectId) => {
    console.log("error");
    navigate(`/dashboard/ProjectChapters?projectId=${projectId}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    let filtered = allProjects;

    if (searchName.trim() !== '') {
      filtered = filtered.filter((p) =>
        p.name?.toLowerCase().includes(searchName.toLowerCase())
      );
    }
    if (searchStatus.trim() !== '') {
      filtered = filtered.filter((p) =>
        p.projectStatus?.toLowerCase().includes(searchStatus.toLowerCase())
      );
    }
    setProjects(filtered);
  };

  const handleReset = () => {
    setSearchName('');
    setSearchStatus('');
    setProjects(allProjects);
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'DONE':
        return { bg: '#62e666ff', color: '#1B5E20' };
      case 'IN_PROGRESS':
        return { bg: '#6cb0e7ff', color: '#0D47A1' };
      case 'IN_EDITING':
        return { bg: '#e9a239ff', color: '#E65100' };
      case 'PLANNING':
        return { bg: '#d85ceeff', color: '#4A148C' };
      default:
        return { bg: '#E0E0E0', color: '#424242' };
    }
  };

  // === Summary counts ===
  const totalCount = allProjects.length;
  const planningCount = allProjects.filter((p) => p.projectStatus === 'PLANNING').length;
  const progressCount = allProjects.filter((p) => p.projectStatus === 'IN_PROGRESS').length;
  const editingCount = allProjects.filter((p) => p.projectStatus === 'IN_EDITING').length;
  const doneCount = allProjects.filter((p) => p.projectStatus === 'DONE').length;

  // === Table rows ===
  const projectRows = projects.map((proj) => {
    const statusStyle = getStatusColor(proj.projectStatus);
    return {
      'Project Name': (
        <span
          role="button"
          tabIndex={0}
          onClick={() => handleProjectClick(proj.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleProjectClick(proj.id);
          }}
          style={{
            cursor: 'pointer',
            color: '#000',
            fontWeight: 500,
            textDecoration: 'underline',
          }}
          title="Click to see chapter details"
        >
          {proj.name}
        </span>
      ),
      'Project Status': (
        <span
          style={{
            backgroundColor: statusStyle.bg,
            color: statusStyle.color,
            padding: '4px 10px',
            borderRadius: '8px',
            fontWeight: 500,
            textTransform: 'capitalize',
          }}
        >
          {proj.projectStatus || 'N/A'}
        </span>
      ),
      Deadline: Array.isArray(proj.deadline)
        ? `${proj.deadline[2]}-${proj.deadline[1]}-${proj.deadline[0]}`
        : 'N/A',
      Action: (
        <div>
          <FaEdit
            style={{ cursor: 'pointer', marginRight: '10px', color: '#1976d2' }}
            // onClick={() => handleEditProject(proj.id)}
            title="Edit Project"
          />
          <FaTrash
            style={{ cursor: 'pointer', color: '#e53935' }}
            onClick={() => handleDeleteProject(proj.id)}
            title="Delete Project"
          />
        </div>
      ),
    };
  });

  return (
    <>
      {isLoading && <Loader />}
      <ToastContainer />
      <Helmet>
        <title>Project List</title>
      </Helmet>

      <Container sx={{ mt: 3, mb: 6 }}>
        {/* ===== SUMMARY CARDS ===== */}
        <Grid container spacing={3} mb={4}>
          {[
            { title: 'Total Projects', count: totalCount, gradient: 'linear-gradient(135deg, #E3F2FD, #BBDEFB)' },
            { title: 'Planning', count: planningCount, gradient: 'linear-gradient(135deg, #EDE7F6, #D1C4E9)' },
            { title: 'In Progress', count: progressCount, gradient: 'linear-gradient(135deg, #E1F5FE, #B3E5FC)' },
            { title: 'In Editing', count: editingCount, gradient: 'linear-gradient(135deg, #FFF3E0, #FFE0B2)' },
            { title: 'Completed', count: doneCount, gradient: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)' },
          ].map((card, idx) => (
            <Grid item xs={12} sm={6} md={2.4} key={idx}>
              <Paper
                elevation={4}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: '16px',
                  background: card.gradient,
                  color: '#333',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 6,
                  },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: '600' }}>
                  {card.title}
                </Typography>
                <Typography variant="h4" sx={{ mt: 1, fontWeight: '700' }}>
                  {card.count}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ===== HEADER ===== */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={4}
          flexWrap="wrap"
          rowGap={2}
        >
          <Typography variant="h4" gutterBottom>
            Project List
          </Typography>

          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={handleNewProject}
            sx={{ borderRadius: '8px', textTransform: 'none', px: 3 }}
          >
            Create Project
          </Button>
        </Stack>

        {/* ===== SEARCH FORM ===== */}
        <form onSubmit={handleSearch} style={{ marginBottom: '20px' }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={2}>
            <TextField
              name="projectName"
              label="Project Name"
              variant="outlined"
              style={{ width: '260px' }}
              onChange={(e) => setSearchName(e.target.value)}
              value={searchName}
            />
            <TextField
              name="projectStatus"
              label="Project Status"
              variant="outlined"
              style={{ width: '260px' }}
              onChange={(e) => setSearchStatus(e.target.value)}
              value={searchStatus}
            />
            <Button type="submit" variant="contained" sx={{ height: '40px' }}>
              Search
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              sx={{ height: '40px' }}
            >
              Reset
            </Button>
          </Stack>
        </form>

        {/* ===== TABLE SECTION ===== */}
        <Card>
          {projectRows.length > 0 ? (
            <TableData data={projectRows} headers={headers} rowsPerPageOptions={[5, 10, 20]} />
          ) : (
            <Typography variant="body1" align="center" sx={{ p: 3, color: 'text.secondary' }}>
              No Projects Found
            </Typography>
          )}
        </Card>
      </Container>
    </>
  );
}
